import { Link, Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { MoreHorizontal, Plus } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { SegmentedTabs } from '~/components/common/segmented-tabs';
import { Button } from '~/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu';
import { Skeleton } from '~/components/ui/skeleton';

import { useNeedsYouCount } from '../hooks/use-needs-you-count';
import { isUnder } from '../utils/nav';
import { useNavContext } from '../utils/nav-context';
import { formatCount } from '../utils/run-format';

import { PageHelpProvider } from './page-help';

/** The catalog-enrichment sections, in the order an operator works: runs first, setup last. */
const SECTIONS = [
  { value: 'runs', to: '/catalog-enrichment/runs', labelKey: 'nav.runs' },
  { value: 'products', to: '/catalog-enrichment/products', labelKey: 'nav.products' },
  {
    value: 'schedules',
    to: '/catalog-enrichment/schedules',
    labelKey: 'nav.schedules',
  },
  {
    value: 'experiments',
    to: '/catalog-enrichment/experiments',
    labelKey: 'nav.experiments',
  },
  {
    value: 'feed-audit',
    to: '/catalog-enrichment/feed-audit',
    labelKey: 'nav.feedAudit',
  },
] as const satisfies readonly { value: string; to: string; labelKey: string }[];

type Section = (typeof SECTIONS)[number]['value'];

/**
 * True inside one run (`/catalog-enrichment/runs/<id>/…`, not `/runs/new`): the run frame draws
 * its own cards, and the page owns its primary action.
 */
function isInsideRun(pathname: string): boolean {
  return /^\/catalog-enrichment\/runs\/(?!new(?:\/|$))[^/]+/.test(pathname);
}

/** Create pages own their primary action (Create batch …), so the header's New batch hides. */
function isCreatePage(pathname: string): boolean {
  return /\/new\/?$/.test(pathname);
}

/** The section the URL is in; Runs when none matches (the `/catalog-enrichment` index redirects there).
 *  The tab always shows where you ARE (a product page highlights Products); where you came from
 *  is the page's own "← Back to <origin>" chip. `originSection` only applies on pages that belong
 *  to no section of their own. */
function activeSection(pathname: string, originSection: Section | null): Section {
  // Cost decisions belong to Batches: a cost gate is a batch decision.
  if (isUnder(pathname, '/catalog-enrichment/cost-approvals')) return 'runs';
  // Held back (and a listing page) is a view of Products (guide part 1 §5.3).
  if (
    isUnder(pathname, '/catalog-enrichment/needs-attention') ||
    isUnder(pathname, '/catalog-enrichment/feed-products')
  ) {
    return 'products';
  }
  return SECTIONS.find((s) => isUnder(pathname, s.to))?.value ?? originSection ?? 'runs';
}

/** Maps a parsed `NavOrigin.kind` to the top-nav section it should highlight. */
function sectionForOrigin(kind: string | undefined): Section | null {
  switch (kind) {
    case 'batch':
      return 'runs';
    case 'attention':
      return 'products';
    case 'experiment':
      return 'experiments';
    default:
      return null;
  }
}

/** List pages on the Batches design lay out their own cards, so the section card is dropped. */
const BARE_LISTS = [
  '/catalog-enrichment',
  '/catalog-enrichment/runs',
  '/catalog-enrichment/cost-approvals',
  '/catalog-enrichment/feed-audit',
  '/catalog-enrichment/experiments',
  '/catalog-enrichment/needs-attention',
  '/catalog-enrichment/products',
  '/catalog-enrichment/schedules',
  // New batch / New audit lay out their own section cards and summary rail.
  '/catalog-enrichment/runs/new',
  '/catalog-enrichment/feed-audit/new',
];

/** True on one of those list pages (not their nested routes). */
function isBareList(pathname: string): boolean {
  const path = pathname.replace(/\/$/, '');
  // A schedule's own page lays out its own card too.
  return BARE_LISTS.includes(path) || path.startsWith('/catalog-enrichment/schedules/');
}

/** Every namespace the section's pages read. */
const CE_NAMESPACES = [
  'catalog-enrichment',
  'catalog-enrichment-content-gen',
  'catalog-enrichment-curation',
  'catalog-enrichment-experiments',
  'catalog-enrichment-lineage-images',
  'catalog-enrichment-needs-attention',
  'catalog-enrichment-product-master',
  'catalog-enrichment-recovery',
  'catalog-enrichment-run',
  'catalog-enrichment-runs',
  'catalog-enrichment-schedules',
];

/**
 * The CE section shell, mounted by `/catalog-enrichment/route.tsx` (design guide §2.3): one sticky
 * 56px bar — the section name, the section nav as underline tabs (also inside a batch, so any
 * section is one click away) and the section's one primary — then the scrolling body. Batches is
 * the only badged tab: the amber count of batches waiting on you (open gates + cost approvals).
 * The bar never renders raw i18n keys: it shows a skeleton until its namespace has loaded.
 */
export function CatalogEnrichmentLayout() {
  const { t, ready: navReady } = useTranslation('catalog-enrichment');
  // Every CE namespace is loaded up front: pages read several (run, runs, product-master…), and a
  // page drawn before its namespace lands flashes raw keys ("runTabs.overview") for seconds.
  const { ready: allReady } = useTranslation(CE_NAMESPACES);
  const ready = navReady && allReady;
  const { pathname } = useLocation();
  const navigate = useNavigate();
  // S8 — the origin section (Batches / Needs attention / Experiments) when this page was reached
  // via a cross-section `from` link, so the tab strip agrees with the breadcrumb/back chip.
  const { origin } = useNavContext();
  const originSection = sectionForOrigin(origin?.kind);
  const section = activeSection(pathname, originSection);
  const needsYou = useNeedsYouCount() ?? 0;
  const insideRun = isInsideRun(pathname);
  const showPrimary = section === 'runs' && !isCreatePage(pathname);
  const bare = isBareList(pathname);

  // Larger small-text scale for the catalog preview (utilities.css `html.catalog-enrichment-type`) — on
  // <html> so portalled popovers/dialogs get it too; removed when leaving the section.
  useEffect(() => {
    document.documentElement.classList.add('catalog-enrichment-type');
    return () => {
      document.documentElement.classList.remove('catalog-enrichment-type');
    };
  }, []);

  const options = SECTIONS.map((s) => ({
    value: s.value,
    label: t(s.labelKey),
    // Only work waiting on a person earns a badge — a plain total is not something to act on.
    ...(s.value === 'runs' && needsYou > 0
      ? { badge: formatCount(needsYou), badgeTone: 'attention' as const }
      : {}),
  }));

  return (
    <PageHelpProvider>
      <div className="bg-gradient-page flex h-full flex-col">
        <header className="bg-background/90 sticky top-0 z-30 shrink-0 border-b backdrop-blur">
          {ready ? (
            <div className="mx-auto flex h-14 max-w-[1440px] items-end gap-8 px-4 sm:px-8">
              <div className="flex shrink-0 items-center gap-2 self-center">
                <p
                  className="text-foreground text-base font-semibold"
                  data-testid="catalog-enrichment-title"
                >
                  {t('title')}
                </p>
                {/* The module is still in preview alongside the legacy catalog screens. */}
                <span
                  className="bg-accent-100 text-primary rounded-full px-2 py-0.5 text-xs font-medium"
                  data-testid="catalog-enrichment-preview-badge"
                >
                  {t('previewBadge')}
                </span>
              </div>
              <nav
                aria-label={t('nav.ariaLabel')}
                className="scrollbar-thin min-w-0 self-stretch overflow-x-auto"
              >
                <SegmentedTabs
                  variant="underline"
                  size="sm"
                  value={section}
                  testId="catalog-enrichment-nav"
                  onChange={(value) => {
                    const target = SECTIONS.find((s) => s.value === value);
                    if (target) void navigate({ to: target.to });
                  }}
                  options={options}
                  ariaLabel={t('nav.ariaLabel')}
                  className="h-full"
                />
              </nav>
              {showPrimary && !insideRun && (
                <div className="ml-auto flex shrink-0 items-center gap-2 self-center">
                  {/* Rarely used destinations stay out of the tab band (guide part 1 §5.1). */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-9 cursor-pointer"
                        aria-label={t('header.more')}
                        data-testid="catalog-enrichment-header-more-button"
                      >
                        <MoreHorizontal aria-hidden="true" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem asChild className="cursor-pointer">
                        <Link
                          to="/catalog-enrichment/cost-approvals"
                          data-testid="catalog-enrichment-header-more-cost"
                        >
                          {t('header.costDecisions')}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild className="cursor-pointer">
                        <Link
                          to="/catalog-enrichment/runs"
                          search={{ view: 'failures' }}
                          data-testid="catalog-enrichment-header-more-failures"
                        >
                          {t('header.failurePatterns')}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild className="cursor-pointer">
                        <Link
                          to="/catalog-enrichment/runs"
                          search={{ archived: 'ARCHIVED', status: 'ALL' }}
                          data-testid="catalog-enrichment-header-more-archived"
                        >
                          {t('header.archived')}
                        </Link>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button
                    asChild
                    className="cursor-pointer"
                    data-testid="catalog-enrichment-header-primary-button"
                  >
                    <Link to="/catalog-enrichment/runs/new" aria-keyshortcuts="n">
                      <Plus aria-hidden="true" />
                      {t('newBatch')}
                    </Link>
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div
              aria-hidden="true"
              className="mx-auto h-14 max-w-[1440px] px-4 sm:px-8"
              data-testid="catalog-enrichment-header-loading"
            />
          )}
        </header>

        <div className="min-h-0 min-w-0 flex-1 overflow-auto">
          <div className="mx-auto max-w-[1440px] px-4 pt-6 pb-12 sm:px-8">
            {!ready ? (
              <div className="flex flex-col gap-4" aria-busy="true">
                <Skeleton className="h-28 w-full rounded-xl" />
                <Skeleton className="h-96 w-full rounded-xl" />
              </div>
            ) : bare || insideRun ? (
              <Outlet />
            ) : (
              <div className="bg-card min-w-0 rounded-2xl border p-6 shadow-sm">
                <Outlet />
              </div>
            )}
          </div>
        </div>
      </div>
    </PageHelpProvider>
  );
}
